import { defineStore } from 'pinia'
import HttpClient from '~/services/http-client.js'
import { useToast } from 'vue-toastification'
import ApiRoutes from '~/services/api-routes.js'

export const useAuthStore = defineStore('authStore', () => {
  const isLoading = ref(false)
  const loadingIndicator = useLoadingIndicator()
  const user = ref(null)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isLoggedIn = computed(() => !!user.value)

  const hydrate = () => {
    if (!import.meta.client) return
    const raw = localStorage.getItem('user')
    user.value = raw ? JSON.parse(raw) : null
  }

  const login = async (email, password) => {
    isLoading.value = true
    loadingIndicator.start()

    // Čteme před requestem, po awaitu už nemusí být Nuxt kontext k dispozici.
    // Cílem je stránka, ze které nás guard poslal na login, jinak domů.
    const router = useRouter()
    const redirectTo = loginRedirectTarget(useRoute().query.redirect)

    const response = await HttpClient.post(ApiRoutes.AUTH_LOGIN, {
      email,
      password
    })

    if (response.ok) {
      user.value = response.data.user
      if (import.meta.client) {
        localStorage.setItem('user', JSON.stringify(response.data.user))
      }
      await router.push(redirectTo)
      useToast().success('Byli jste přihlášeni')
    }

    isLoading.value = false
    loadingIndicator.finish()
  }

  const requestPasswordReset = async (email) => {
    isLoading.value = true

    try {
      const response = await HttpClient.post(ApiRoutes.AUTH_PASSWORD, { email })
      if (response.ok) {
        useToast().success('Odeslali jsme mail')
        return true
      }
      return false
    } finally {
      isLoading.value = false
    }
  }

  const confirmPasswordReset = async (token, password) => {
    isLoading.value = true

    try {
      const response = await HttpClient.post(ApiRoutes.AUTH_PASSWORD_CONFIRM, {
        token,
        password
      })
      return !!response.ok
    } finally {
      isLoading.value = false
    }
  }

  const clearAuthData = async () => {
    const response = await HttpClient.post(ApiRoutes.AUTH_LOGOUT)
    user.value = null
    if (import.meta.client) {
      localStorage.removeItem('user')
      sessionStorage.clear()
    }
    if (response.ok) {
      useToast().success('Byli jste odhlášeni')
    }
  }

  return {
    isLoading,
    user,
    isAdmin,
    isLoggedIn,
    hydrate,
    login,
    clearAuthData,
    requestPasswordReset,
    confirmPasswordReset
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
